// components/EditLivestreamModal.jsx
import React, { useEffect, useState } from 'react';
import { FaTimes, FaCheck, FaSearch, FaUser, FaTags } from 'react-icons/fa';
import { getComediantes } from '../../../../../components/http/Actions/ComediantesAction';
import { getTags } from '../../../../../components/http/Actions/TagsAction';

const EditLivestreamModal = ({ livestreamToEdit, setLivestreamToEdit, handleEditSubmit, isLoading, onClose }) => {
  const [comediantes, setComediantes] = useState([]);
  const [tags, setTags] = useState([]);
  const [loadingResources, setLoadingResources] = useState(true);
  const [error, setError] = useState('');
  const [searchComediante, setSearchComediante] = useState('');
  const [searchTag, setSearchTag] = useState('');
  
  // Estados para la paginación
  const [comediantesPage, setComediantesPage] = useState(1);
  const [showSelectedComediantesOnly, setShowSelectedComediantesOnly] = useState(false);
  const itemsPerPage = 10; // Número de comediantes por página

  // Cargar comediantes y tags
  useEffect(() => {
    const fetchResources = async () => {
      try {
        // Cargar comediantes
        await getComediantes(
          (data) => setComediantes(data.data || []),
          (err) => setError('Error al cargar comediantes: ' + err)
        );
        
        // Cargar tags
        await getTags(
          (data) => setTags(data.data || []),
          (err) => setError('Error al cargar tags: ' + err)
        );
      } catch (err) {
        setError('Error al cargar recursos');
      } finally {
        setLoadingResources(false);
      }
    };
    
    fetchResources();
  }, []);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    
    if (name === "precio") {
      // Asegurar que precio sea un número
      setLivestreamToEdit(prev => ({
        ...prev,
        [name]: parseFloat(value) || 0
      }));
    } else {
      setLivestreamToEdit(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleComedianteChange = (comedianteId) => {
    if ((livestreamToEdit.ComedianteIds || []).includes(comedianteId)) {
      // Quitar comediante
      setLivestreamToEdit(prev => ({
        ...prev,
        ComedianteIds: (prev.ComedianteIds || []).filter(id => id !== comedianteId)
      }));
    } else {
      // Agregar comediante
      setLivestreamToEdit(prev => ({
        ...prev,
        ComedianteIds: [...(prev.ComedianteIds || []), comedianteId]
      }));
    }
  };

  const handleTagChange = (tagId) => {
    if ((livestreamToEdit.TagIds || []).includes(tagId)) {
      // Quitar tag
      setLivestreamToEdit(prev => ({
        ...prev,
        TagIds: (prev.TagIds || []).filter(id => id !== tagId)
      }));
    } else {
      // Agregar tag
      setLivestreamToEdit(prev => ({
        ...prev,
        TagIds: [...(prev.TagIds || []), tagId]
      }));
    }
  };

  // Función para determinar si un color es oscuro
  const isColorDark = (hexColor) => {
    // Si no tiene formato hex o es inválido, retornamos falso
    if (!hexColor || !hexColor.startsWith('#')) return false;
    
    // Convertir hex a RGB
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    
    // Calcular luminosidad
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Si luminance < 0.5, el color es considerado oscuro
    return luminance < 0.5;
  };

  // Filtrado de comediantes con opción de mostrar solo seleccionados
  const filteredComediantes = comediantes.filter(comediante => {
    const matchesSearch = comediante.nombre.toLowerCase().includes(searchComediante.toLowerCase());
    const isSelected = (livestreamToEdit.ComedianteIds || []).includes(comediante.comedianteId);
    
    if (showSelectedComediantesOnly) {
      return isSelected && matchesSearch;
    }
    return matchesSearch;
  });

  // Lógica de paginación para comediantes
  const totalPages = Math.ceil(filteredComediantes.length / itemsPerPage);
  const paginatedComediantes = filteredComediantes.slice(
    (comediantesPage - 1) * itemsPerPage,
    comediantesPage * itemsPerPage
  );

  // Filtrado de tags
  const filteredTags = tags.filter(tag => 
    tag.nombre.toLowerCase().includes(searchTag.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 overflow-y-auto backdrop-blur-sm">
      <div className="bg-zinc-800 rounded-xl shadow-2xl max-w-4xl w-full p-8 my-8 max-h-[90vh] overflow-y-auto border border-zinc-700">
        <div className="flex justify-between items-center mb-8">
          <h3 className="text-2xl font-bold text-white">Editar Livestream</h3>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-white hover:bg-zinc-700 p-2 rounded-full transition-all duration-200"
            aria-label="Cerrar"
          >
            <FaTimes />
          </button>
        </div>
        
        {error && (
          <div className="bg-red-500/20 border border-red-500 text-red-100 px-4 py-3 rounded-lg mb-6">
            <p className="text-sm">{error}</p>
          </div>
        )}
        
        {loadingResources ? (
          <div className="flex justify-center my-8">
            <div className="w-12 h-12 border-4 border-t-4 border-gray-200 border-t-red-600 rounded-full animate-spin"></div>
          </div>
        ) : (
          <form onSubmit={(e) => handleEditSubmit(e, livestreamToEdit)} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Título
                </label>
                <input
                  type="text"
                  name="titulo"
                  value={livestreamToEdit.titulo || ''}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-zinc-700/50 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Fecha
                </label>
                <input
                  type="datetime-local"
                  name="fecha"
                  value={livestreamToEdit.fecha || ''}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-zinc-700/50 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  URL de Imagen
                </label>
                <input
                  type="url"
                  name="imagen"
                  value={livestreamToEdit.imagen || ''}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-zinc-700/50 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  URL del Stream
                </label>
                <input
                  type="url"
                  name="videoURL"
                  value={livestreamToEdit.videoURL || ''}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-zinc-700/50 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Precio (0 para gratis)
                </label>
                <input
                  type="number"
                  name="precio"
                  value={livestreamToEdit.precio || 0}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-3 bg-zinc-700/50 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Estado
                </label>
                <select
                  name="estado"
                  value={livestreamToEdit.estado || 'Próximo'}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-zinc-700/50 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                  required
                >
                  <option value="Próximo">Próximo</option>
                  <option value="EnVivo">En Vivo</option>
                  <option value="Finalizado">Finalizado</option>
                </select>
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">
                Descripción
              </label>
              <textarea
                name="descripcion"
                value={livestreamToEdit.descripcion || ''}
                onChange={handleChange}
                rows="4"
                className="w-full px-4 py-3 bg-zinc-700/50 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 resize-none"
                required
              ></textarea>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* COMEDIANTES - CON MEJOR CONTRASTE */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-white flex items-center">
                    <FaUser className="mr-2 text-red-500" />
                    Comediantes
                  </label>
                  <span className="bg-red-600/20 text-red-300 text-xs px-2 py-1 rounded-full">
                    {(livestreamToEdit.ComedianteIds || []).length} seleccionados
                  </span>
                </div>
                
                {/* Barra de búsqueda con filtro para solo mostrar seleccionados */}
                <div className="flex items-center space-x-2">
                  <div className="relative flex-grow">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaSearch className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="Buscar comediante..."
                      value={searchComediante}
                      onChange={(e) => {
                        setSearchComediante(e.target.value);
                        setComediantesPage(1); // Reset a primera página al buscar
                      }}
                      className="pl-10 w-full px-4 py-2 bg-zinc-700/30 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-red-500 text-sm"
                    />
                  </div>
                  
                  {/* Botón para filtrar solo los seleccionados */}
                  <button
                    type="button"
                    onClick={() => setShowSelectedComediantesOnly(!showSelectedComediantesOnly)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 flex items-center space-x-1
                      ${showSelectedComediantesOnly 
                        ? 'bg-red-600 text-white' 
                        : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'}`}
                  >
                    <FaCheck className={`h-3 w-3 ${showSelectedComediantesOnly ? 'opacity-100' : 'opacity-50'}`} />
                    <span>Seleccionados</span>
                  </button>
                </div>
                
                {/* Lista de comediantes con mejor contraste */}
                <div className="rounded-lg border border-zinc-700 bg-zinc-800/50 overflow-hidden">
                  {/* Conteo de resultados y paginación */}
                  <div className="flex justify-between items-center px-3 py-2 bg-zinc-700/30 border-b border-zinc-700 text-xs text-gray-300">
                    <span>
                      {filteredComediantes.length} resultado{filteredComediantes.length !== 1 ? 's' : ''}
                    </span>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        disabled={comediantesPage === 1}
                        onClick={() => setComediantesPage(prev => Math.max(prev - 1, 1))}
                        className="w-6 h-6 flex items-center justify-center rounded hover:bg-zinc-600 disabled:opacity-50 disabled:hover:bg-transparent"
                      >
                        &lt;
                      </button>
                      <span>
                        {comediantesPage} / {totalPages || 1}
                      </span>
                      <button
                        type="button"
                        disabled={comediantesPage >= totalPages}
                        onClick={() => setComediantesPage(prev => Math.min(prev + 1, totalPages))}
                        className="w-6 h-6 flex items-center justify-center rounded hover:bg-zinc-600 disabled:opacity-50 disabled:hover:bg-transparent"
                      >
                        &gt;
                      </button>
                    </div>
                  </div>
                  
                  {/* Lista de comediantes */}
                  <div className="max-h-48 overflow-y-auto pr-2 space-y-1 custom-scrollbar p-2">
                    {paginatedComediantes.length === 0 ? (
                      <p className="text-gray-400 text-sm p-2 text-center">
                        No se encontraron resultados
                      </p>
                    ) : (
                      paginatedComediantes.map((comediante) => {
                        const isSelected = (livestreamToEdit.ComedianteIds || []).includes(comediante.comedianteId);
                        const isPrincipal = isSelected && (livestreamToEdit.ComedianteIds || [])[0] === comediante.comedianteId;
                        
                        return (
                          <div
                            key={comediante.comedianteId}
                            onClick={() => handleComedianteChange(comediante.comedianteId)}
                            className={`flex items-center justify-between p-2 rounded-md cursor-pointer transition-all duration-200 ${
                              isSelected ? 'bg-red-900/30 border border-red-600/50' : 'hover:bg-zinc-700/50 border border-zinc-700'
                            }`}
                          >
                            <span className="text-sm text-gray-200">
                              {comediante.nombre}
                              {isPrincipal && (
                                <span className="ml-1 text-xs text-red-400">(Principal)</span>
                              )}
                            </span>
                            <span className={`flex items-center justify-center w-5 h-5 rounded-full 
                              ${isSelected ? 'bg-red-600' : 'bg-zinc-700 border border-zinc-600'}`}>
                              {isSelected && <FaCheck className="h-3 w-3 text-white" />}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>
                  
                  {/* Lista rápida de seleccionados */}
                  {(livestreamToEdit.ComedianteIds || []).length > 0 && (
                    <div className="px-3 py-2 bg-zinc-700/30 border-t border-zinc-700">
                      <p className="text-xs text-gray-400 mb-1">Seleccionados:</p>
                      <div className="flex flex-wrap gap-1">
                        {(livestreamToEdit.ComedianteIds || []).map((id, index) => {
                          const comediante = comediantes.find(c => c.comedianteId === id);
                          if (!comediante) return null;
                          
                          return (
                            <span 
                              key={id}
                              className="inline-flex items-center bg-red-900/20 text-red-300 text-xs px-2 py-1 rounded-full"
                            >
                              {comediante.nombre}
                              {index === 0 && (
                                <span className="ml-1 text-xs text-red-400">(Principal)</span>
                              )}
                              <FaTimes 
                                className="ml-1 cursor-pointer hover:text-white" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleComedianteChange(id);
                                }}
                              />
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
{/* TAGS - MEJORADOS PARA MEJOR VISIBILIDAD */}
<div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-white flex items-center">
                    <FaTags className="mr-2 text-red-500" />
                    Tags
                  </label>
                  <span className="bg-red-600/20 text-red-300 text-xs px-2 py-1 rounded-full">
                    {(livestreamToEdit.TagIds || []).length} seleccionados
                  </span>
                </div>
                
                {/* Barra de búsqueda para tags */}
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaSearch className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Buscar tag..."
                    value={searchTag}
                    onChange={(e) => setSearchTag(e.target.value)}
                    className="pl-10 w-full px-4 py-2 bg-zinc-700/30 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-red-500 text-sm"
                  />
                </div>
                
                {/* Tags mejorados con mejor visibilidad */}
                <div className="rounded-lg border border-zinc-700 bg-zinc-800/50 overflow-hidden">
                  <div className="max-h-48 overflow-y-auto p-3 custom-scrollbar">
                    {filteredTags.length === 0 ? (
                      <p className="text-gray-400 text-sm text-center">No se encontraron resultados</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {filteredTags.map((tag) => {
                          const isSelected = (livestreamToEdit.TagIds || []).includes(tag.tagId);
                          const isDark = isColorDark(tag.colorHex);
                          
                          return (
                            <div
                              key={tag.tagId}
                              onClick={() => handleTagChange(tag.tagId)}
                              className={`inline-flex items-center rounded-lg px-3 py-2 text-sm transition-all duration-200 cursor-pointer shadow-lg ${
                                isSelected ? 'ring-2' : 'hover:ring-1'
                              }`}
                              style={{ 
                                backgroundColor: `${tag.colorHex}20`,
                                borderColor: tag.colorHex,
                                color: isDark ? '#ffffff' : tag.colorHex,
                                border: `1px solid ${tag.colorHex}`,
                                ringColor: tag.colorHex,
                              }}
                            >
                              <span className="font-medium" style={{ 
                                textShadow: isDark ? '0 0 10px rgba(0,0,0,0.8)' : 'none',
                              }}>
                                {tag.nombre}
                              </span>
                              {isSelected && (
                                <span className="ml-2 flex items-center justify-center w-5 h-5 rounded-full" 
                                  style={{ 
                                    backgroundColor: isDark ? '#ffffff' : tag.colorHex,
                                    color: isDark ? tag.colorHex : '#ffffff',
                                  }}
                                >
                                  <FaCheck className="h-3 w-3" />
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  
                  {/* Lista de tags seleccionados */}
                  {(livestreamToEdit.TagIds || []).length > 0 && (
                    <div className="px-3 py-2 bg-zinc-700/30 border-t border-zinc-700">
                      <p className="text-xs text-gray-400 mb-1">Seleccionados:</p>
                      <div className="flex flex-wrap gap-1">
                        {(livestreamToEdit.TagIds || []).map(id => {
                          const tag = tags.find(t => t.tagId === id);
                          if (!tag) return null;
                          
                          const isDark = isColorDark(tag.colorHex);
                          
                          return (
                            <span 
                              key={id}
                              className="inline-flex items-center text-xs px-2 py-1 rounded-full shadow-md"
                              style={{ 
                                backgroundColor: `${tag.colorHex}40`,
                                borderColor: tag.colorHex,
                                color: isDark ? '#ffffff' : tag.colorHex,
                                border: '1px solid',
                                textShadow: isDark ? '0 0 8px rgba(0,0,0,0.7)' : 'none',
                              }}
                            >
                              <span className="font-medium">{tag.nombre}</span>
                              <FaTimes 
                                className="ml-1 cursor-pointer hover:text-white" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTagChange(id);
                                }}
                              />
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-4 pt-4 border-t border-zinc-700">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600 transition-all duration-200"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isLoading || (livestreamToEdit.ComedianteIds || []).length === 0 || (livestreamToEdit.TagIds || []).length === 0}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center min-w-[140px] transition-all duration-200"
                title={(livestreamToEdit.ComedianteIds || []).length === 0 ? 'Selecciona al menos un comediante' : 
                       (livestreamToEdit.TagIds || []).length === 0 ? 'Selecciona al menos un tag' : ''}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Guardando...
                  </>
                ) : (
                  'Guardar Cambios'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EditLivestreamModal;